import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import * as yup from "yup"
import { Appearance, TouchableOpacityProps, ViewStyle } from "react-native"
import { observer } from "mobx-react-lite"
import { color, nativeBaseTheme } from "../../theme"
import { Text } from "../text/text"
import { Radio, View, Modal } from "native-base"
import { Formik, FormikProps, FormikValues } from "formik"
import { FormControl } from "../form-control/form-control"
import { translate } from "../../i18n"
import { FormProps } from "../form/form"
import { Button } from "../button/button"
import { DateTimePickerField } from "../field/date-time-picker-field/date-time-picker-field"
import { NumeralField } from "../field/numeral-field/numeral-field"
import { useStores } from "../../models"
import JalaAnalytic from "../../utils/jala-analytic"
// import { goBack } from "../../navigators"
import { AutoFeederPond } from "../../database"
import moment from "moment"
import numeral from "numeral"

const FORM_CONTROL: ViewStyle = {
  paddingHorizontal: 16,
  // marginBottom: 8,
  paddingBottom: 12,
  backgroundColor: color.palette.white
}

const BUTTON: ViewStyle = {
  marginVertical: 8,
  marginHorizontal: 16
}

export interface CalibrationModalFormProps extends FormProps {
  /**
   * An optional style override useful for padding & margin.
   */
  initialValues?: {
    mode?: "normal"|"continuous",
    condition?: "under"|"over"
    actualEndTime?: string
    actualIntervalPeriod?: number
    remainingQuantity?: number
    expectedQuantity?: number
  }
}

const CalibrationModalForm = observer(function CalibrationModalForm(props: CalibrationModalFormProps) {
  const {
    colorScheme = "light" || Appearance.getColorScheme(),
    onSubmit,
    initialValues: initialValuesProps = {},
    innerRef,
    ...rest
  } = props

  const validationSchema = yup.object({
    mode: yup.string().nullable(),
    condition: yup.string().nullable().required(),
    actualEndTime: yup.string().nullable()
      .when(['mode', 'condition'], {
        is: (mode: string, condition: string) => mode === "continuous" && condition === "under",
        then: (schema) => schema.required(),
      })
      .label(translate("autoFeederPondDetailScreen.actualEndTime")),
    actualIntervalPeriod: yup.number().nullable()
      .when(['mode', 'condition'], {
        is: (mode: string, condition: string) => mode === "normal" && condition === "under",
        then: (schema) => schema.required(),
      })
      .label(translate("autoFeederPondDetailScreen.actualIntervalPeriod")),
    remainingQuantity: yup.number().nullable()
      .when('condition', {
        is: "over",
        then: (schema) => schema.required(),
      })
      .label(translate("autoFeederPondDetailScreen.remainingQuantity")),
    expectedQuantity: yup.number().nullable()
      .when('condition', {
        is: "over",
        then: (schema) => schema.required(),
      })
      .label(translate("autoFeederPondDetailScreen.expectedQuantity")),
  })

  const initialValues = useMemo(() => ({
    condition: "under",
    actualEndTime: null,
    actualIntervalPeriod: null,
    remainingQuantity: null,
    expectedQuantity: null,
    ...initialValuesProps,
    mode: initialValuesProps?.mode || "normal",
  }), [initialValuesProps])

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      innerRef={innerRef}
      {...rest}
    >
      {({ values, setFieldValue }) => (
        <>
          <FormControl
            labelTx="autoFeederPondDetailScreen.calibrationChooseTitle"
            colorScheme={colorScheme}
            labelColor={color.palette.gray800}
            style={FORM_CONTROL}
          >
            <Radio.Group
              colorScheme={colorScheme}
              name="condition"
              accessibilityLabel={translate("farmShrimpPriceForm.source")}
              value={values.condition}
              onChange={(value) => {
                setFieldValue('condition', value)
              }}
            >
              <Radio value="under" mr="32px" mb="8px">
                <Text tx="autoFeederPondDetailScreen.under" preset="body2" ml="6px" colorScheme={colorScheme} />
              </Radio>
              <Radio value="over">
                <Text tx="autoFeederPondDetailScreen.over" preset="body2" ml="6px" colorScheme={colorScheme} />
              </Radio>
            </Radio.Group>
          </FormControl>
          {values.condition === "under" && values.mode === "normal" && <NumeralField
            name="actualIntervalPeriod"
            txPrefix="autoFeederPondDetailScreen"
            colorScheme={colorScheme}
            numeralInputProps={{
              placeholder: '-',
              InputRightElement: <View pr="8px">
                <Text text="mnt" preset="title4" color={color.palette.gray800} />
              </View>
            }}
          />}
          {values.condition === "under" && values.mode === "continuous" && <DateTimePickerField
            name="actualEndTime"
            txPrefix="autoFeederPondDetailScreen"
            formControlProps={{
              labelTx: "autoFeederPondDetailScreen.actualEndTimeAsk",
            }}
            colorScheme={colorScheme}
            dateTimePickerProps={{ dateTimePickerProps: { mode: "time" } }}
          />}
          {values.condition === "over" && (<>
            <NumeralField
              name="expectedQuantity"
              txPrefix="autoFeederPondDetailScreen"
              colorScheme={colorScheme}
              numeralInputProps={{
                placeholder: '-',
                InputRightElement: <View pr="8px">
                  <Text text="kg" preset="title4" color={color.palette.gray800} />
                </View>
              }}
            />
            <NumeralField
              name="remainingQuantity"
              txPrefix="autoFeederPondDetailScreen"
              colorScheme={colorScheme}
              numeralInputProps={{
                placeholder: '-',
                InputRightElement: <View pr="8px">
                  <Text text="kg" preset="title4" color={color.palette.gray800} />
                </View>
              }}
            />
          </>)}
        </>
      )}
    </Formik>
  )
})

export const AutoFeederCalibrationModal: FC<{
  colorScheme?: "light" | "dark",
  isOpen?: boolean,
  onClose?: (success?: boolean) => void,
  model: AutoFeederPond
} & Omit<TouchableOpacityProps, "onPress">> = observer(function AutoFeederCalibrationModal(props) {
  const {
    colorScheme = "light" || Appearance.getColorScheme(),
    isOpen,
    onClose,
    model,
    children
  } = props
  const { autoFeederPondStore: modelStore } = useStores()
  const [ data, setData ] = useState<any>(null)

  const modelForm = useRef<FormikProps<FormikValues>>()
  const modelFormRef = useCallback((node) => {
    modelForm.current = node;
  }, [])

  const initialValues = useMemo(() => ({
    mode: (model.state?.mode || "normal") as "normal"|"continuous",
    condition: "under" as "under"|"over",
    actualEndTime: model.state?.endTime,
    actualIntervalPeriod: model.state?.intervalPeriod,
    remainingQuantity: null,
    expectedQuantity: (model.state?.feeds || []).find((f: any) => f.feed_at === moment().format('YYYY-MM-DD'))?.quantity,
  }), [model.state?.endTime, model.state?.feeds, model.state?.intervalPeriod, model.state?.mode])

  const onSubmit = useCallback(async (data) => {
    setData(data)
  }, [])

  const handleSubmit = useCallback((event) => {
    if (modelForm.current) modelForm.current.handleSubmit(event)
  }, [])

  const debit = useMemo(() => {
    if (!data) return null
    return model.calculateDebit({
      condition: data.condition,
      actualEndTime: data.actualEndTime,
      actualIntervalPeriod: data.actualIntervalPeriod,
      remainingQuantity: data.remainingQuantity,
      expectedQuantity: data.expectedQuantity
    })
  }, [data, model])

  const onConfirmed = useCallback(async () => {
    modelStore.set('loading', true)
    const state = { ...model.state, debit }
    const success = await modelStore.update(model, { state })

    JalaAnalytic.log("Mobile app: Form On Submit", {
      form: CalibrationModalForm.displayName || 'Unknown',
      data: JSON.stringify(data || null),
      success,
    })

    if (success) {
      onClose && onClose(success)
    }
    modelStore.set('loading', false)
  }, [data, debit, model, modelStore, onClose])

  const onBack = useCallback(() => {
    setData(null)
  }, [])

  useEffect(() => {
    if (isOpen) setData(null)
  }, [isOpen])

  return (<>
    <View>
      {children}
    </View>
    <Modal isOpen={isOpen} onClose={onClose}>
      <View
        alignSelf="stretch"
        marginY="80px"
        marginX="20px"
        backgroundColor={nativeBaseTheme.colors.light["50"]}
        borderRadius={10}
      >
        {!data && <>
          <View borderBottomWidth={1} borderBottomColor={color.palette.gray300}
                backgroundColor={color.palette.orange300} padding="12px" borderTopRadius={10}>
            <Text tx="autoFeederPondDetailScreen.calibrationModalTitle" preset="title4" color={color.palette.black} />
            <Text tx="autoFeederPondDetailScreen.calibrationModalSubtitle" preset="body3" color={color.palette.black} />
          </View>
          <View paddingBottom="12px" backgroundColor={color.palette.white} borderBottomRadius={10}>
            <CalibrationModalForm
              colorScheme={colorScheme}
              innerRef={modelFormRef}
              onSubmit={onSubmit}
              initialValues={initialValues}
            />
            <Button
              testID="jala:id/save-button"
              tx="save"
              colorScheme={colorScheme}
              loading={modelStore.loading}
              onPress={handleSubmit}
              style={BUTTON}
            />
          </View>
        </>}
        {data && <>
          <Text tx="autoFeederPondDetailScreen.confirmationTitle" paddingTop="12px" paddingX="12px" preset="title3" color={color.palette.black} />
          <View backgroundColor={color.palette.gray100} marginX="12px" marginTop="8px" padding="8px" borderRadius="4px">
            {data.condition === 'under' && <Text textAlign="center">
              <Text
                tx={data.mode === 'normal' ? "autoFeederPondDetailScreen.actualIntervalPeriod" : "autoFeederPondDetailScreen.actualEndTime"}
                textAlign="center"
                preset="title4"
                color={color.palette.black}
              />
              <Text text=": " preset="title4" color={color.palette.black} colorScheme={colorScheme} />
              <Text
                text={data.mode === 'normal' ? `${numeral(data.actualIntervalPeriod).format('0,0.[00]')} mnt` : `${moment(data.actualEndTime, 'HH:mm:ss').format('HH:mm')}`}
                preset="title4"
                color={color.palette.black}
              />
            </Text>}
            {data.condition === 'over' && <Text textAlign="center">
              <Text
                tx="autoFeederPondDetailScreen.remainingQuantity"
                preset="title4"
                color={color.palette.black}
              />
              <Text text=": " preset="title4" color={color.palette.black} colorScheme={colorScheme} />
              <Text
                text={`${numeral(data.remainingQuantity).format('0,0.[00]')} kg`}
                preset="title4"
                color={color.palette.black}
              />
            </Text>}
          </View>
          <View backgroundColor={color.palette.gray100} marginX="12px" marginTop="8px" marginBottom="12px" padding="8px" borderRadius="4px">
            <Text tx="autoFeederPondDetailScreen.changes" textAlign="center" marginBottom="8px" preset="title4" color={color.palette.black} />
            <View flexDirection="row">
              <View flex={1}>
                <Text tx="autoFeederPondDetailScreen.debitBefore" textAlign="center" preset="title5" color={color.palette.black} />
                <Text textAlign="center">
                  <Text text={numeral(model?.throwDebit).format('0,0')} textAlign="center" preset="title5" color={color.palette.black} />
                  <Text text=" "/>
                  <Text tx="autoFeederPondDetailScreen.throwDebitUnit" textAlign="center" preset="title5" color={color.palette.gray700} />
                </Text>
              </View>
              <View alignItems="center" justifyContent="center">
                <Text fontSize="2xl" colorScheme={colorScheme}>→</Text>
              </View>
              <View flex={1}>
                <Text tx="autoFeederPondDetailScreen.debitAfter" textAlign="center" preset="title5" color={color.palette.black} />
                <Text textAlign="center">
                  <Text text={numeral(debit * (model?.state?.onTime || 1)).format('0,0')} textAlign="center" preset="title5" color={color.palette.black} />
                  <Text text=" "/>
                  <Text tx="autoFeederPondDetailScreen.throwDebitUnit" textAlign="center" preset="title5" color={color.palette.gray700} />
                </Text>
              </View>
            </View>
          </View>
          <View paddingBottom="12px" backgroundColor={color.palette.white} borderBottomRadius={10}>
            <Button
              testID="jala:id/save-button"
              text={`${translate("common.yes")}, ${translate("save")}`}
              colorScheme={colorScheme}
              loading={modelStore.loading}
              onPress={onConfirmed}
              style={BUTTON}
            />
            <Button
              testID="jala:id/back-button"
              tx="common.back"
              preset="outline"
              colorScheme={colorScheme}
              disabled={modelStore.loading}
              onPress={onBack}
              style={BUTTON}
            />
          </View>
        </>}
      </View>
    </Modal>
  </>)
})
